// ---------------------------------------------------------------------------
// UX-template wizard data layer: Draft <-> schema-v2 config_json.
//
// The wizard edits a FLAT draft of only the fields a user can actually change, and derives
// the rest. That split is not a simplification of the model — it IS the model: the spec's
// UxTemplate carries ~45 fields but the mock's wizard writes 27 of them and computes or
// constants the remainder (see plan.md "Scope rule: rendered controls only"). Editing the
// nested config directly would expose fields no control drives, which is how you end up with
// a portal that appears to configure things the runtime ignores.
//
// The derivations below are the load-bearing part. `interactive` and `promptCondition` are
// what the PSADT template switches DeployMode on, so getting them wrong makes a template
// silently silent (or silently interactive) with nothing in the UI to show why:
//
//     interactive false                  -> Silent      (no dialog at all)
//     interactive true  + ifRunning      -> Auto        (dialog only when the app is in use)
//     interactive true  + always         -> Interactive (dialog on every run)
//
// `interactive` (the draft field below) is the ADMINISTRATOR'S control and picks between the last
// two. The sidecar's `interactive` is a different question — "does this deployment show anything
// at all" — and stays derived from the mode. Conflating them is what put the progress window
// behind the prompt condition.
//
// APPEARANCE (identity.*) is part of the draft. Two different things get called "branding" and
// only one of them lives here:
//
//   device-wide branding — branding_templates -> a signed override config at
//                          %ProgramData%\sap\psadt\config, layer 3, LOCAL WINS, applies to every
//                          package on the device. Not this file.
//   deployment-time identity — identity.* below, baked onto the install command as
//                          -DeploymentConfig b64: at publish, per template. THIS file.
//
// The IMAGE itself is not a template field: publish auto-attaches the caller tenant's
// BrandingAsset logo (publishing.py) and embeds it as `branding.logoB64`. What the template
// decides is which PSADT asset slot that image fills — `render()` derives
// branding.assetKind from dialogStyle + logoSource. So `logo`/`banner` stay null here: a
// per-template filename would be a field no upload writes.
// ---------------------------------------------------------------------------

export type CloseAppsMode = 'perform' | 'autoClose' | 'skip' | 'notify'
export type OnTimeout = 'defer' | 'proceed'
/** PSADT's two dialog engines. 'modern' IS Fluent — the backend's own vocabulary
 *  (_V2_DIALOG_STYLES), kept verbatim so no translation layer is needed. */
export type DialogStyle = 'modern' | 'classic'
export type LogoSource = 'appIcon' | 'custom'

/** The only PSADT engine we ship. Rendered read-only in the wizard; the backend rejects
 *  anything else outright rather than silently ignoring it. */
export const PINNED_PSADT_VERSION = '4.1.8'

/** Every PSADT engine this product ships. The backend accepts exactly these
 *  (template_service._V2_PSADT_VERSION), so the picker offers exactly these. */
export const SHIPPED_PSADT_VERSIONS = [PINNED_PSADT_VERSION] as const

/** Timeout choices the wizard offers, in minutes. PSADT's own shipped default is 3300s (55
 *  min); 5 is an intentional product choice and a real behaviour change, so it is surfaced
 *  rather than buried. */
export const TIMEOUT_CHOICES = [5, 10, 15, 30, 60] as const

/** Shipped default identity. `companyName` is REQUIRED by the backend (it is the visible dialog
 *  subtitle, so a blank would ship PSADT's own 'PSAppDeployToolkit' to end users) — this is the
 *  value every template written before the Appearance step existed already carries, so making the
 *  field editable changes nothing until an admin edits it. */
export const DEFAULT_COMPANY_NAME = 'Splashtop Agentic Packr'
/** PSADT's own documented UI.FluentAccentColor example (config.psd1 `0xFF0078D7`). Must match
 *  template_service._V2_DEFAULT_ACCENT. */
export const DEFAULT_FLUENT_ACCENT = '#0078D7'

/** Accent presets, PSADT's default first. Fluent only; Classic ignores the accent entirely. */
export const ACCENT_PRESETS = [
  '#0078D7', '#008272', '#6B69D6', '#10893E', '#C19C00', '#C42B1C', '#5C2D91',
] as const

/**
 * The languages PSADT 4.1.8 ships UI strings for — the exact UI.LanguageOverride table, and it
 * MUST stay identical to template_service._V2_LANGUAGES: the backend hard-ERRORS on a code
 * outside it, so offering a 28th would be a save that always 400s. Parity-tested.
 */
export const PSADT_LANGS: { code: string; label: string }[] = [
  { code: 'ar', label: 'Arabic' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'cs', label: 'Czech' },
  { code: 'da', label: 'Danish' },
  { code: 'de', label: 'German' },
  { code: 'el', label: 'Greek' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fi', label: 'Finnish' },
  { code: 'fr', label: 'French' },
  { code: 'he', label: 'Hebrew' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'it', label: 'Italian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'lv', label: 'Latvian' },
  { code: 'nb', label: 'Norwegian (Bokmål)' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pl', label: 'Polish' },
  { code: 'pt', label: 'Portuguese (Portugal)' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)' },
  { code: 'ru', label: 'Russian' },
  { code: 'sk', label: 'Slovak' },
  { code: 'sv', label: 'Swedish' },
  { code: 'tr', label: 'Turkish' },
  { code: 'zh-CN', label: 'Chinese (Simplified)' },
  { code: 'zh-HK', label: 'Chinese (Traditional)' },
]

export interface UxDraft {
  name: string
  description: string
  /** Which PSADT engine renders the dialogs. */
  psadtVersion: string
  /** Install side */
  mode: CloseAppsMode
  countdownSeconds: number
  forceCountdown: boolean
  allowDefer: boolean
  deferCount: number
  onTimeout: OnTimeout
  notifyTimeoutMinutes: number
  persistPrompt: boolean
  showProgress: boolean
  /** Dialogs on every run, rather than only when a watched app is running. Decides the IDLE case
   *  ONLY: what a busy user sees is `mode`'s decision in either state of this. */
  interactive: boolean
  removeDesktopShortcuts: boolean
  /** Appearance (identity.*) — shared by BOTH deployment types, since it is package-level */
  companyName: string
  dialogStyle: DialogStyle
  /** Fluent accent, #RRGGBB. Ignored by Classic. */
  fluentAccent: string
  /** Which image fills the Fluent logo slot: the packaged AppIcon.png, or the tenant's
   *  BrandingAsset logo. Classic ignores it — render() routes the tenant image to the banner
   *  slot on style alone. */
  logoSource: LogoSource
  language: string /** 'auto' or a PSADT language code */
  /** UI.BalloonNotifications — the tray notifications PSADT raises when a deployment starts and
   *  when it finishes. On in PSADT's own config, so `true` is the value that carries nothing. */
  balloonNotifications: boolean
  /** Uninstall side — its own PSADT deployment type, so its own experience, and the same
   *  fields as the install side rather than a reduced set of them. A removal that could not
   *  be given its own wait or its own timeout policy was one where those values still
   *  existed: the install step's decided both deployment types. */
  uninstallMode: CloseAppsMode
  uninstallCountdownSeconds: number
  uninstallForceCountdown: boolean
  uninstallAllowDefer: boolean
  uninstallDeferCount: number
  uninstallOnTimeout: OnTimeout
  uninstallNotifyTimeoutMinutes: number
  uninstallPersistPrompt: boolean
  uninstallShowProgress: boolean
  /** The removal's own answer to the same question. */
  uninstallInteractive: boolean

  /** Shipped behaviour the wizard renders NO control for, carried through verbatim so saving a
   *  template does not delete a setting its author configured elsewhere. `restart` is the only
   *  way the product asks a user about a reboot (and countdownSeconds 0 is the postpone path);
   *  `schedule` is the end-user datetime picker. Both stay readable per the scope rule — out of
   *  scope means "build no control", not "remove the capability". */
  carried: Record<string, any>
}

/** Whether a mode puts a dialog on screen at all. Only these two do. */
export const showsDialog = (m: CloseAppsMode): boolean => m === 'notify' || m === 'autoClose'

/** Must match template_service._V2_CARRIED_KEYS — the backend accepts exactly these alongside
 *  the v2 shape, so sending a key that is not here 400s the save. */
export const CARRIED_KEYS = ['restart', 'schedule'] as const

export const emptyDraft = (): UxDraft => ({
  name: '',
  description: '',
  psadtVersion: PINNED_PSADT_VERSION,
  mode: 'notify',
  // SHORTER THAN notifyTimeoutMinutes, and it has to stay that way. The countdown runs inside the
  // welcome dialog and the dialog's own expiry is the wait; when the two are equal the expiry is
  // what fires, so the deployment ends at exit code 1618 having installed nothing. These two
  // defaults were both 300, which made every template created from them behave that way -- three
  // of one tenant's three templates, and a real LastPass deployment that retried four times and
  // never ran an installer.
  countdownSeconds: 240,
  // A dialog that times out spends a deferral, so an unattended machine burns its whole budget
  // without anyone choosing to. `proceed` only avoids that with `forceCountdown` beside it:
  // -CloseProcessesCountdown is suppressed while deferrals remain, and only
  // -ForceCloseProcessesCountdown runs the timer from the start.
  forceCountdown: true,
  allowDefer: true,
  deferCount: 3,
  onTimeout: 'proceed',
  notifyTimeoutMinutes: 5,
  persistPrompt: false,
  showProgress: true,
  // OFF by default, which is what every template written before this control existed already does:
  // interrupt a busy user, leave an idle one alone.
  interactive: false,
  removeDesktopShortcuts: false,
  // The identity every template written before the Appearance step existed already carries, so a
  // template created after this change is byte-identical to one created before it.
  companyName: DEFAULT_COMPANY_NAME,
  // F-4: the mock defaults to 'modern' (its UxTemplateWizard.tsx:100). Ours said 'classic', which
  // was a discrepancy, not a choice worth keeping. Existing templates are unaffected — draftFrom
  // reads whatever they stored.
  dialogStyle: 'modern',
  fluentAccent: DEFAULT_FLUENT_ACCENT,
  logoSource: 'appIcon',
  language: 'auto',
  balloonNotifications: true,
  // A removal is not a negotiation: the application is going away either way, so the default
  // warns and closes it rather than asking permission that has only one useful answer. The
  // install side still asks — the two steps default differently because they are different
  // decisions, not because one was copied from the other.
  uninstallMode: 'autoClose',
  // `autoClose` force-closes when this expires, so it needs a real duration: zero means no timer
  // runs at all, which leaves the dialog waiting for the user this mode assumed was not there.
  // Shorter than uninstallNotifyTimeoutMinutes, for the reason given at countdownSeconds:
  // the removal's dialog expires against its own wait and takes its countdown with it.
  uninstallCountdownSeconds: 240,
  uninstallForceCountdown: false,
  uninstallAllowDefer: false,
  uninstallDeferCount: 1,
  // Only reached if the removal is switched to the prompting mode, where a dialog nobody answers
  // must not close the application behind the user's back.
  uninstallOnTimeout: 'defer',
  uninstallNotifyTimeoutMinutes: 5,
  uninstallPersistPrompt: false,
  uninstallShowProgress: true,
  uninstallInteractive: false,
  carried: {},
})

/**
 * Countdown options, verbatim from the mock (sap-mockup/components/UxTemplateWizard.tsx:121).
 * Four values, no zero: on the mock's model "wait for a response" is the dialog TIMEOUT on the
 * install step, not a countdown of length nothing.
 */
export const COUNTDOWNS = [
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
  { value: 1800, label: '30 minutes' },
] as const

/** A countdown length as the wizard writes it: "4 minutes", "1 minute", "48 seconds". */
export const countdownLabel = (seconds: number): string => {
  if (seconds % 60 !== 0) return `${seconds} seconds`
  const minutes = seconds / 60
  return minutes === 1 ? '1 minute' : `${minutes} minutes`
}

/**
 * The countdown select's options: COUNTDOWNS plus the stored value when it is not one of them
 * (the 240 s default, or a value the wizard derived from the wait), so the select shows what the
 * template ships instead of falling back to its first option.
 */
export const countdownChoices = (current: number): { value: number; label: string }[] => {
  const options: { value: number; label: string }[] = COUNTDOWNS.map((o) => ({ ...o }))
  if (current > 0 && !options.some((o) => o.value === current)) {
    options.push({ value: current, label: countdownLabel(current) })
    options.sort((a, b) => a.value - b.value)
  }
  return options
}

/**
 * The welcome dialog's own timeout — UI.DefaultTimeout, surfaced as "Wait for a response" in the
 * prompting mode. Install and uninstall are separate PSADT runs and carry a timeout each.
 *
 * The countdown runs INSIDE that dialog, so one that cannot finish strictly before the wait ends
 * — equal included, since the dialog times out exactly as the countdown reaches zero — would
 * leave the deployment at exit 1618. The countdown is never shortened for it: the renderer ships
 * the wait extended past the countdown instead (template_service.dialog_timeout_for), which is
 * what lets the countdown mode offer the countdown alone.
 *
 * The mock's `promptTimeoutSeconds` (:133).
 */
export const promptTimeoutSeconds = (notifyTimeoutMinutes: number): number =>
  notifyTimeoutMinutes * 60

/** Uppercase #RRGGBB, or the shipped default. Mirrors the backend, which normalizes with
 *  `accent.upper()` and warns-and-replaces anything that is not #RRGGBB — matching it here is
 *  what keeps a no-op open/save from rewriting the stored value (`<input type="color">` hands
 *  back lowercase). */
export const normalizeAccent = (hex: unknown): string =>
  typeof hex === 'string' && /^#[0-9A-Fa-f]{6}$/.test(hex)
    ? hex.toUpperCase()
    : DEFAULT_FLUENT_ACCENT

/** Read an existing template's config_json back into a draft. Accepts schema 1 as well, so an
 *  admin can open a 013-era template without it appearing blank. */
export function draftFrom(config?: any, name = '', description = ''): UxDraft {
  const d = emptyDraft()
  d.name = name
  d.description = description
  if (!config) return d
  d.psadtVersion = config.psadtVersion || PINNED_PSADT_VERSION

  // Read BEFORE the schema split, because both schemas store these at the top level and both
  // must survive an edit. Dropping them here is what silently deleted a v1 template's restart
  // prompt when it was re-saved as v2.
  for (const k of CARRIED_KEYS) {
    if (config[k] != null) d.carried[k] = config[k]
  }

  if (config.schema_version >= 2) {
    d.mode = config.closeApps?.mode ?? d.mode
    d.countdownSeconds = config.closeApps?.countdownSeconds ?? d.countdownSeconds
    d.forceCountdown = !!config.closeApps?.forceCountdown
    d.allowDefer = !!config.defer?.allow
    d.deferCount = config.defer?.count ?? d.deferCount
    d.onTimeout = config.defer?.onTimeout ?? d.onTimeout
    d.notifyTimeoutMinutes = config.timers?.notifyTimeoutMinutes ?? d.notifyTimeoutMinutes
    d.persistPrompt = !!config.welcome?.persistPrompt
    d.showProgress = config.dialogs?.showProgress ?? d.showProgress
    // OFF unless the template says otherwise. onlyIfAppsToClose is still read where a template
    // carries it, because every template saved before this control existed states its install
    // timing through that key — reading it is what keeps such a template rendering as it did.
    d.interactive =
      config.dialogs?.interactive ??
      (showsDialog(d.mode) && config.welcome?.onlyIfAppsToClose === false)
    d.removeDesktopShortcuts = !!config.postInstall?.removeDesktopShortcuts
    // Appearance. Narrowed rather than trusted: config_json is whatever the API last stored, and
    // an out-of-range dialogStyle would otherwise reach a <select> that has no such option and
    // render blank.
    d.companyName = config.identity?.companyName || d.companyName
    d.dialogStyle = config.identity?.dialogStyle === 'modern' ? 'modern' : 'classic'
    d.fluentAccent = normalizeAccent(config.identity?.fluentAccent)
    d.logoSource = config.identity?.logoSource === 'custom' ? 'custom' : 'appIcon'
    d.language = config.identity?.languageOverride ?? 'auto'
    // Only an explicit false turns them off, so a template stored before this setting existed keeps
    // PSADT's behaviour rather than silently losing its notifications.
    d.balloonNotifications = config.identity?.balloonNotifications !== false
    d.uninstallMode = config.uninstall?.mode ?? d.uninstallMode
    d.uninstallCountdownSeconds = config.uninstall?.countdownSeconds ?? d.uninstallCountdownSeconds
    d.uninstallForceCountdown = !!config.uninstall?.forceCountdown
    d.uninstallAllowDefer = !!config.uninstall?.allowDefer
    d.uninstallDeferCount = config.uninstall?.deferCount ?? d.uninstallDeferCount
    // A template written before the removal had a wait of its own was capped by the install's, so
    // that is the value it means. Reading it keeps such a template's dialog the same length.
    d.uninstallNotifyTimeoutMinutes =
      config.uninstall?.notifyTimeoutMinutes ?? d.notifyTimeoutMinutes
    // Same for the timeout policy: those templates expressed it as a countdown on the removal
    // dialog, which is precisely what "proceed on timeout" renders.
    d.uninstallOnTimeout =
      config.uninstall?.onTimeout ??
      (d.uninstallMode === 'notify' && d.uninstallCountdownSeconds > 0 ? 'proceed' : 'defer')
    d.uninstallPersistPrompt = !!config.uninstall?.persistPrompt
    d.uninstallShowProgress = config.uninstall?.showProgress ?? d.uninstallShowProgress
    // The removal has no timing field of its own, so the only signal the wire carries is the mode
    // it was sent as — an authored `notify` removal that asked for this ships as `autoClose`.
    d.uninstallInteractive =
      config.uninstall?.interactive ?? d.uninstallMode === 'autoClose'
    return d
  }

  // Schema 1 (feature 013): interactive + welcome.*. Map what maps; a v1 template has no
  // uninstall experience at all, so those keep their defaults rather than inventing values.
  d.mode = config.interactive ? 'notify' : 'perform'
  d.allowDefer = !!config.welcome?.allowDefer
  d.deferCount = config.welcome?.deferTimes ?? d.deferCount
  // `closeProcessesCountdown` FIRST. It is PSADT's own parameter name; `welcome.countdownSeconds` is
  // a second name WE invented for the same value (both feed $AEAMCountdownSeconds in the runtime), and
  // the mock has no such field — its countdown control is `closeApps.countdownSeconds`, which is read
  // on the schema-2 path above. Preferring our alias here meant a v1 template carrying both (a real
  // 45-second countdown plus a materialised `countdownSeconds: 0`) imported as 0 and the admin's
  // countdown silently became "no countdown". Reading the native name first makes the import agree
  // with what the device actually does with such a config.
  d.countdownSeconds =
    config.welcome?.closeProcessesCountdown ?? config.welcome?.countdownSeconds ?? 0
  d.persistPrompt = !!config.welcome?.persistPrompt
  d.showProgress = config.progress?.enabled ?? d.showProgress
  return d
}

/**
 * Build the schema-v2 config_json the backend expects.
 *
 * Everything not in the draft is either DERIVED here or written as the documented constant.
 * The constants are not laziness: each one is a field the spec models but no control drives,
 * so emitting anything else would claim a capability the UI does not offer.
 */
export function toConfig(d: UxDraft): Record<string, any> {
  const timeoutSeconds = (d.notifyTimeoutMinutes || 1) * 60
  const uninstallTimeoutSeconds = (d.uninstallNotifyTimeoutMinutes || 1) * 60

  // Deferral is only meaningful in `notify`: every other mode either shows no dialog or
  // force-closes on a timer, so a Defer button could not be honoured. The backend enforces
  // this too; doing it here keeps the saved config honest about what the user will see.
  const deferAllowed = d.mode === 'notify' && d.allowDefer
  const uninstallDeferAllowed = d.uninstallMode === 'notify' && d.uninstallAllowDefer

  return {
    // Carried first so an explicit v2 key below always wins if the two ever overlap.
    ...d.carried,
    schema_version: 2,
    psadtVersion: d.psadtVersion || PINNED_PSADT_VERSION,
    identity: {
      // Required by the backend, so a blank field falls back rather than 400ing on save: the
      // company name IS the dialog subtitle, and an empty one ships PSADT's own
      // 'PSAppDeployToolkit' to end users.
      companyName: d.companyName.trim() || DEFAULT_COMPANY_NAME,
      dialogStyle: d.dialogStyle,
      // Classic ignores logoSource entirely (render() routes the tenant image to the banner slot
      // on style alone), so pin it to the default there rather than storing a choice that has no
      // effect and would resurface if the style were switched back.
      logoSource: d.dialogStyle === 'modern' ? d.logoSource : 'appIcon',
      // NOT a per-template asset: publish attaches the caller tenant's BrandingAsset logo and
      // embeds it as branding.logoB64. A filename here would be a field no upload ever writes.
      logo: null,
      banner: null,
      fluentAccent: normalizeAccent(d.fluentAccent),
      languageOverride: d.language === 'auto' ? null : d.language,
      balloonNotifications: d.balloonNotifications,
    },
    welcome: {
      persistPrompt: d.mode === 'notify' && d.persistPrompt,
      // Derived from the modes, never typed.
      showOnInstall: showsDialog(d.mode),
      showOnUninstall: showsDialog(d.uninstallMode),
      // AUTHORED, not inferred. This used to read `!showProgress && mode === 'notify'`, so asking
      // for a progress window silently bought an always-prompt and turning one off silently took it
      // away — neither written anywhere an administrator could see. `dialogs.interactive` is the
      // control that decides it now; this stays in step for readers seeded from the older key.
      //
      // Scoped to the modes that show a dialog, exactly as `dialogs.interactive` below is. Writing
      // the raw draft field here instead let a silent mode store "shown on every run", which the
      // reopened draft could not recover — an open-and-save with no edits changed the template.
      // A PROGRESS WINDOW KEEPS THE DEPLOYMENT VISIBLE, where that costs nothing. 'ifRunning' is
      // DeployMode Auto, and Auto becomes Silent on a device with nothing to close — which skips
      // the progress call, so a window the administrator switched ON never appears. With no
      // postponement an Interactive install draws no welcome there either, so the window is gained
      // and nothing unasked-for is shown. WITH postponement it would draw a postpone dialog, so
      // there it is left alone and the backend warns instead.
      onlyIfAppsToClose: !(
        showsDialog(d.mode) && (d.interactive || (d.showProgress && !deferAllowed))
      ),
    },
    closeApps: {
      mode: d.mode,
      // autoClose counts down to a force-close; notify only needs a countdown when the
      // timeout should PROCEED rather than defer.
      // The mock emits the install countdown as chosen (its UxTemplateWizard.tsx:301); only the
      // uninstall one is capped by the dialog timeout.
      countdownSeconds:
        d.mode === 'autoClose'
          ? d.countdownSeconds
          : d.mode === 'notify' && d.onTimeout === 'proceed'
            ? timeoutSeconds
            : 0,
      // Picks WHICH PSADT countdown parameter is emitted: force = visible immediately even
      // while deferrals remain; plain = only once deferral is gone.
      forceCountdown:
        d.mode === 'autoClose' ||
        (d.mode === 'notify' && d.onTimeout === 'proceed' && d.allowDefer && d.forceCountdown),
    },
    defer: {
      allow: deferAllowed,
      count: deferAllowed ? d.deferCount : 0,
      onTimeout: d.onTimeout,
    },
    timers: { notifyTimeoutMinutes: d.notifyTimeoutMinutes },
    dialogs: {
      showProgress: showsDialog(d.mode) && d.showProgress,
      // The control, written as authored. It picks the TIMING of the dialogs the mode describes;
      // whether there are any is the mode's decision, which is why it is scoped to the modes that
      // show one.
      interactive: showsDialog(d.mode) && d.interactive,
    },
    // THE SAME DERIVATION AS THE INSTALL SIDE ABOVE, field for field, because it is the same
    // dialog with a different deployment type behind it. The removal used to collapse the
    // timeout policy into "is the countdown non-zero", which meant a removal could not be given
    // the one behaviour the install defaults to (wait, then close and proceed) without an
    // administrator working out that a duration was standing in for a decision.
    uninstall: {
      mode: d.uninstallMode,
      countdownSeconds:
        d.uninstallMode === 'autoClose'
          ? d.uninstallCountdownSeconds
          : d.uninstallMode === 'notify' && d.uninstallOnTimeout === 'proceed'
            ? uninstallTimeoutSeconds
            : 0,
      forceCountdown:
        d.uninstallMode === 'autoClose' ||
        (d.uninstallMode === 'notify' &&
          d.uninstallOnTimeout === 'proceed' &&
          d.uninstallAllowDefer &&
          d.uninstallForceCountdown),
      allowDefer: uninstallDeferAllowed,
      deferCount: uninstallDeferAllowed ? d.uninstallDeferCount : 0,
      onTimeout: d.uninstallOnTimeout,
      // UI.DefaultTimeout for the removal run. Install and uninstall are separate PSADT
      // processes, so each carries its own.
      notifyTimeoutMinutes: d.uninstallNotifyTimeoutMinutes,
      persistPrompt: d.uninstallMode === 'notify' && d.uninstallPersistPrompt,
      showProgress: showsDialog(d.uninstallMode) && d.uninstallShowProgress,
      interactive: showsDialog(d.uninstallMode) && d.uninstallInteractive,
    },
    postInstall: { removeDesktopShortcuts: d.removeDesktopShortcuts },
  }
}
